import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import apiClient from '@/utils/api';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const sectionInfo = {
  hero: { title: 'Hero Slider', description: 'Homepage hero slider with background images' },
  about: { title: 'About Us', description: 'Company overview and mission statement' },
  milestones: { title: 'Milestones', description: 'Company achievements and statistics' },
  services: { title: 'Our Services', description: 'Services and workspace offerings' },
  locations: { title: 'Our Locations', description: 'Office locations across cities' },
  spaces: { title: 'Our Spaces', description: 'Available workspace types' },
  testimonials: { title: 'Testimonials', description: 'Client reviews and feedback' },
  blog: { title: 'Latest Insights (Blog)', description: 'Blog posts and articles' },
  contact: { title: 'Get In Touch', description: 'Contact information and form' },
  map: { title: 'Find Us (Map)', description: 'Interactive location map' },
};

export default function AdminSectionVisibility() {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    try {
      const response = await axios.get(`${API}/sections/visibility`);
      setSections(response.data);
    } catch (error) {
      console.error('Error fetching sections:', error);
      toast.error('Failed to load section visibility');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = async (sectionName, currentValue) => {
    const newValue = !currentValue;
    
    // Optimistic update
    setSections(sections.map(s => 
      s.section_name === sectionName ? { ...s, is_visible: newValue } : s
    ));

    try {
      await apiClient.put(`/admin/sections/visibility/${sectionName}`, {
        is_visible: newValue
      });
      toast.success(`${sectionInfo[sectionName]?.title || sectionName} ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      // Revert on error
      setSections(sections.map(s => 
        s.section_name === sectionName ? { ...s, is_visible: currentValue } : s
      ));
      toast.error('Failed to update section visibility');
      console.error('Error updating section:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Section Visibility</h1>
            <p className="text-gray-600 mt-1">Control which sections appear on your homepage</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="space-y-4">
            {sections.map((section) => {
              const info = sectionInfo[section.section_name] || { 
                title: section.section_name, 
                description: 'Homepage section' 
              };
              
              return (
                <div
                  key={section.section_name}
                  className={`flex items-center justify-between p-4 rounded-lg border-2 transition-all ${
                    section.is_visible 
                      ? 'border-green-200 bg-green-50' 
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {info.title}
                      </h3>
                      {section.is_visible ? (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                          Visible
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-semibold rounded-full">
                          Hidden
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 text-sm">{info.description}</p>
                  </div>
                  
                  <button
                    onClick={() => toggleSection(section.section_name, section.is_visible)}
                    className={`ml-4 relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                      section.is_visible ? 'bg-green-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                        section.is_visible ? 'translate-x-7' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Changes take effect immediately on the homepage. Hidden sections will not be visible to website visitors but can still be managed in the admin panel.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
